// Create or edit a Skill (pantallas-abilities-y-skills, 3.3, 3.4).
//
// One form for both: what changes is the starting values and the verb on the
// button. The Abilities it needs and the agents that apply it are chosen here,
// and an agent that lacks one of those Abilities is named before saving -- the
// server refuses it anyway, but saying it here saves a round trip that would
// only end in the same sentence.

import * as React from 'react'
import { useQueries } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { queryKeys } from '@/queries/keys'
import { agentsService } from '@/services/agentsService'
import type { Ability, Agent, Skill, SkillPayload } from '@/types/agent'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The Skill being edited; `null` creates one. */
  skill: Skill | null
  abilities: Ability[]
  agents: Agent[]
  saving: boolean
  /** What the server answered when it refused, if it did. */
  serverError: string | null
  onSave: (payload: SkillPayload) => void
}

const EMPTY: SkillPayload = {
  name: '',
  situation: '',
  procedure: '',
  is_enabled: true,
  ability_ids: [],
  agent_ids: [],
}

function toggled(list: string[], id: string, on: boolean) {
  return on ? [...list, id] : list.filter((item) => item !== id)
}

export function SkillDialog({ open, onOpenChange, skill, abilities, agents, saving, serverError, onSave }: Props) {
  const { t } = useTranslation()
  const [form, setForm] = React.useState<SkillPayload>(EMPTY)
  const [errors, setErrors] = React.useState<{ name?: string; abilities?: string }>({})
  const nameRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!open) return
    setForm(skill ? { ...EMPTY, ...skill } : EMPTY)
    setErrors({})
  }, [open, skill])

  // What each chosen agent has, to say before saving which one lacks what.
  const agentAbilities = useQueries({
    queries: form.agent_ids.map((agentId) => ({
      queryKey: queryKeys.abilities.ofAgent(agentId),
      queryFn: () => agentsService.getAgentAbilities(agentId),
      enabled: open,
    })),
  })
  const lacking = form.agent_ids.flatMap((agentId, index) => {
    const has = agentAbilities[index]?.data
    if (!has) return []
    const owned = new Set(has.map((ability) => ability.id))
    const missing = abilities.filter((a) => form.ability_ids.includes(a.id) && !owned.has(a.id))
    if (missing.length === 0) return []
    const agent = agents.find((a) => a.id === agentId)
    return [{
      agentId,
      text: t('skills_page.form.lacks', {
        agent: agent?.display_name || agent?.name || agentId,
        abilities: missing.map((a) => a.name).join(', '),
      }),
    }]
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const found = {
      name: form.name.trim() ? undefined : t('skills_page.form.name_required'),
      abilities: form.ability_ids.length > 0 ? undefined : t('skills_page.form.abilities_required'),
    }
    setErrors(found)
    if (found.name) {
      nameRef.current?.focus()
      return
    }
    if (found.abilities || lacking.length > 0) return
    onSave({ ...form, name: form.name.trim() })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <form onSubmit={submit} className="space-y-4" noValidate>
          <DialogHeader>
            <DialogTitle>{skill ? t('skills_page.form.title_edit') : t('skills_page.form.title_new')}</DialogTitle>
            <DialogDescription>{t('skills_page.form.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="skill-name">{t('skills_page.form.name')}</Label>
            <Input
              id="skill-name"
              aria-label={t('skills_page.form.name')}
              ref={nameRef}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder={t('skills_page.form.name_placeholder')}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'skill-name-error' : undefined}
            />
            <FieldError id="skill-name-error">{errors.name}</FieldError>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="skill-situation">{t('skills_page.form.situation')}</Label>
            <Textarea
              id="skill-situation"
              aria-label={t('skills_page.form.situation')}
              rows={2}
              value={form.situation}
              onChange={(event) => setForm({ ...form, situation: event.target.value })}
              placeholder={t('skills_page.form.situation_placeholder')}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="skill-procedure">{t('skills_page.form.procedure')}</Label>
            <Textarea
              id="skill-procedure"
              aria-label={t('skills_page.form.procedure')}
              rows={4}
              value={form.procedure}
              onChange={(event) => setForm({ ...form, procedure: event.target.value })}
              placeholder={t('skills_page.form.procedure_placeholder')}
            />
          </div>

          <fieldset className="space-y-2" aria-describedby={errors.abilities ? 'skill-abilities-error' : undefined}>
            <legend className="text-sm font-medium">{t('skills_page.form.abilities')}</legend>
            <p className="text-xs text-muted-foreground">{t('skills_page.form.abilities_hint')}</p>
            {abilities.map((ability) => (
              <div key={ability.id} className="flex items-start gap-2">
                <Checkbox
                  id={`skill-ability-${ability.id}`}
                  aria-label={ability.name}
                  checked={form.ability_ids.includes(ability.id)}
                  onCheckedChange={(on) => setForm({ ...form, ability_ids: toggled(form.ability_ids, ability.id, on === true) })}
                />
                <Label htmlFor={`skill-ability-${ability.id}`} className="font-normal">
                  {ability.name}
                  {ability.description && <span className="block text-xs text-muted-foreground">{ability.description}</span>}
                </Label>
              </div>
            ))}
            <FieldError id="skill-abilities-error">{errors.abilities}</FieldError>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('skills_page.form.agents')}</legend>
            {agents.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('skills_page.form.no_agents')}</p>
            ) : (
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {agents.map((agent) => (
                  <div key={agent.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`skill-agent-${agent.id}`}
                      aria-label={agent.display_name || agent.name}
                      checked={form.agent_ids.includes(agent.id)}
                      onCheckedChange={(on) => setForm({ ...form, agent_ids: toggled(form.agent_ids, agent.id, on === true) })}
                    />
                    <Label htmlFor={`skill-agent-${agent.id}`} className="font-normal">
                      {agent.display_name || agent.name}
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </fieldset>

          {(lacking.length > 0 || serverError) && (
            <div role="alert" className="space-y-1 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {lacking.map((row) => <p key={row.agentId}>{row.text}</p>)}
              {serverError && <p>{serverError}</p>}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('skills_page.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {skill ? t('skills_page.form.save') : t('skills_page.form.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
